import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'
import { fetchDefiLlamaApy } from './defillama'

/**
 * Brix wiTRY — a StakedUSDeV2 clone over iTRY, a **Turkish lira** stablecoin
 * backed by a tokenized basket of Turkish money-market funds
 * (docs.brix.money). Appreciating share price, no rebase. Yield is the NAV
 * delta of those funds: Brix mints the delta as fresh iTRY on a scheduled
 * cycle (weekly at launch) and `transferInRewards` it into the vault, where it
 * vests linearly — exactly Ethena's mechanism.
 *
 * **The rate is denominated in LIRA.** Turkish money-market paper pays 35-45 %
 * because the lira depreciates; a USD holder's return is this number minus the
 * TRY/USD drift, and the two have historically been close. Never present it
 * beside a USD vault's APR without that context — the row's `description` and
 * the term sheet carry it.
 *
 * Rate source, in order:
 *
 *  1. **Brix's own snapshot API** — `/api/witry/apy-snapshot`, whose
 *     `stakingApr` is a FRACTION (`0.3675` = 36.75 %) obtained by simple
 *     annualization of the realized growth over `windowDays`
 *     (`stakingApy` is the same window compounded — verified:
 *     `(1 + apr·d/365)^(365/d) − 1` reproduces it to 5 decimals). We pin
 *     `windowDays=30`, which smooths the weekly distribution cadence and
 *     matched an independent on-chain `convertToAssets` delta over the same
 *     30 days to within 0.1 pp (36.75 % vs 36.68 %) on 2026-08-14.
 *
 *     **Trap:** a window longer than the vault's own life answers **HTTP 503
 *     with the JSON literal `null`** — `windowDays` above ~121 does this
 *     today (launch was 2026-04-23) and the boundary MOVES as the vault ages.
 *     A 30-day window can never cross it, which is the second reason for the
 *     pin; the status check and the `Number(null?.x) → NaN` guard below are
 *     the belt and braces, since `?? 0` on that body would publish a silent
 *     0 % row.
 *
 *  2. **On-chain vesting math** — the Ethena/Neutrl formula
 *     `vestingAmount / VESTING_PERIOD × yearSeconds / totalAssets`. Brix's
 *     `VESTING_PERIOD` is a **private constant with no getter** (both
 *     `vestingPeriod()` and `VESTING_PERIOD()` revert), so it is pinned below
 *     at the value solved bit-exactly from `getUnvestedAmount()`. As with
 *     Neutrl, the divisor degrades to the elapsed time once a cycle runs past
 *     its end, so a stalled distributor decays instead of reporting the last
 *     cycle forever.
 *
 *  3. **DefiLlama** `brix` pool — a percent APY over the same share-price
 *     series, converted to the package's nominal APR.
 */

const CHAIN_ID = Chain.ETHEREUM_MAINNET

/** wiTRY — the ERC-4626 staking vault (Ethereum is the canonical chain; the
 *  MegaETH `wiTRY` at 0x15B271D9… is a bare LayerZero OFT whose `asset()`,
 *  `totalAssets()` and `convertToAssets()` all revert). */
const WITRY: Address = '0xe346c29b5b60ef870b9724c57ccfbbc631e47dee'

const BRIX_APY_URL =
  'https://brix.money/api/witry/apy-snapshot?windowDays=30&method=realized'
const BRIX_DEFILLAMA_POOL = 'da8c4ac9-733d-4a98-85a5-83b76b7e84d1'

/**
 * 248,400 s = 69 h. Solved from `getUnvestedAmount()` against
 * `vestingAmount()` and `lastDistributionTimestamp()` on 2026-08-14 and
 * confirmed by exact integer reproduction of the contract's
 * `(P − elapsed) × vestingAmount / P`; every round candidate (8 h, 1/2/3/7 d)
 * misses. Only reachable through this constant — the vault exposes no getter.
 */
const VESTING_PERIOD = 248_400n

const YEAR_SECONDS = 31_536_000n
const ONE_E18 = 10n ** 18n

export const WITRY_KEY = 'WITRY'
/** Canonical token-list assetGroup (chains 1 and 4326). */
export const WITRY_GROUP_KEY = 'Wrapped iTRY::WITRY'

const STAKED_ITRY_ABI = [
  {
    name: 'vestingAmount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'lastDistributionTimestamp',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'totalAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

/** Brix's own realized-window snapshot. Returns a PERCENT APR. */
const fetchBrixAprPercent = async (): Promise<number> => {
  const res = await fetch(BRIX_APY_URL, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(8000),
  })
  if (!res.ok) throw new Error(`Brix HTTP ${res.status}`)
  // The body for an uncoverable window is the JSON literal `null`, so this
  // optional chain is load-bearing even though the 503 above already caught it.
  const apr = Number((await res.json())?.stakingApr)
  if (!Number.isFinite(apr) || apr <= 0) throw new Error('stakingApr missing')
  return apr * 100
}

/** Ethena-shaped vesting math. Returns a PERCENT APR. */
const fetchWitryAprOnChain = async (): Promise<number> => {
  const [vestingAmount, lastDistribution, totalAssets] =
    (await multicallRetryUniversal({
      chain: CHAIN_ID,
      abi: STAKED_ITRY_ABI,
      calls: [
        { address: WITRY, name: 'vestingAmount', params: [] },
        { address: WITRY, name: 'lastDistributionTimestamp', params: [] },
        { address: WITRY, name: 'totalAssets', params: [] },
      ],
      allowFailure: false,
    })) as [bigint, bigint, bigint]

  if (totalAssets === 0n) throw new Error('wiTRY: empty vault')
  const elapsed = BigInt(Math.floor(Date.now() / 1000)) - lastDistribution
  // Nominal window while the cycle is live; the actual elapsed time once it
  // has run past its end with no new distribution.
  const window = elapsed > VESTING_PERIOD ? elapsed : VESTING_PERIOD
  if (window <= 0n) throw new Error('wiTRY: bad vesting window')
  const perSecond = vestingAmount / window
  const apr = Number((perSecond * YEAR_SECONDS * ONE_E18) / totalAssets) / 1e16
  if (!Number.isFinite(apr) || apr <= 0) throw new Error('wiTRY: no yield')
  return apr
}

export const brixFetcher: YieldFetcher = {
  label: 'WITRY',
  fetch: async () => {
    const apr = await fetchBrixAprPercent()
      .catch(() => fetchWitryAprOnChain())
      .catch(async () =>
        apyToAprPercent(await fetchDefiLlamaApy(BRIX_DEFILLAMA_POOL)),
      )
      .catch(() => 0)
    return { [WITRY_KEY]: apr, [WITRY_GROUP_KEY]: apr }
  },
}
